import { type OAuthError, useAuth0 } from '@auth0/auth0-react';
import React, {
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useSearchParams } from 'react-router';

import { useEnvironmentConfig } from './environment.tsx';

import { extractTokenField } from '#src/utils/token.ts';

export const useLogin = () => {
  const auth = useAuth0();
  const location = useLocation();
  const queryParams = location.search;
  const defaultReturnTo = location.pathname;

  return useMemo(
    () => ({
      login: ({
        returnTo,
        organization,
        invitation,
        includeQueryParams = true, // Whether to include the query params in the returnTo URL
      }: {
        returnTo?: string;
        organization?: string;
        invitation?: string;
        includeQueryParams?: boolean;
      }) =>
        auth.loginWithRedirect({
          appState: {
            returnTo: returnTo
              ? `${returnTo}${includeQueryParams ? queryParams : ''}`
              : `${defaultReturnTo}${includeQueryParams ? queryParams : ''}`,
          },
          authorizationParams: {
            organization,
            invitation,
          },
        }),
    }),
    [auth, defaultReturnTo, queryParams],
  );
};

const useAuth0Permission = () => {
  const {
    auth: { internalTestPartnerUuid, namespace },
  } = useEnvironmentConfig();

  const [permissions, setPermissions] = useState<string[]>([]);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [isInternalTestPartner, setIsInternalTestPartner] = useState(false);
  const [integrationPartnerUuid, setIntegrationPartnerUuid] =
    useState<string>();

  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();

  const refresh = async () => {
    if (!user || !isAuthenticated) {
      return;
    }
    getAccessTokenSilently()
      .then((token) => {
        if (!token) {
          return;
        }
        const impersonating = Boolean(
          extractTokenField(token, 'impersonating'),
        );
        setIsImpersonating(impersonating);

        const auth0Permissions = (extractTokenField(token, 'permissions') ??
          []) as string[];

        const uuid = String(extractTokenField(token, 'integrationPartnerUuid'));
        setIntegrationPartnerUuid(uuid);
        setIsInternalTestPartner(uuid === internalTestPartnerUuid);

        setPermissions(auth0Permissions);
      })
      .catch((e: OAuthError) => {
        // Do not throw an error if the user is not logged in.
        if (e.error === 'login_required') {
          return;
        }
        // eslint-disable-next-line no-console
        console.error(e);
        throw e;
      });
  };

  const value = useMemo(
    () => ({
      permissions,
      setPermissions,
      isImpersonating,
      isInternalTestPartner,
      integrationPartnerUuid,
      refresh,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      permissions,
      setPermissions,
      isImpersonating,
      isInternalTestPartner,
      integrationPartnerUuid,
      namespace,
    ],
  );

  // Load on initial render
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAuthenticated]);

  return value;
};

export const useUserId = (): string => {
  const { user } = useAuth0();

  if (!user?.sub) {
    throw new Error('No user details found in auth token');
  }

  return user.sub;
};

interface PermissionContext {
  permissions: string[];
  setPermissions: (value: React.SetStateAction<string[]>) => void;
  isImpersonating: boolean;
  isInternalTestPartner: boolean;
  integrationPartnerUuid?: string;
  refresh: () => void;
}

const ctx = React.createContext<PermissionContext>({
  permissions: [],
  setPermissions: () => {},
  isImpersonating: false,
  isInternalTestPartner: false,
  refresh: () => {},
});

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps) => {
  const permissions = useAuth0Permission();

  const [searchParams] = useSearchParams();
  const { login } = useLogin();

  // Handle invites for existing users
  useEffect(() => {
    const invitation = searchParams.get('invitation');
    const organization = searchParams.get('organization');

    if (invitation && organization) {
      login({
        organization,
        invitation,
      });
    }
  }, [login, searchParams]);

  return <ctx.Provider value={permissions}>{children}</ctx.Provider>;
};

export const usePermissions = () => useContext(ctx);
